
import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CdkDragDrop, CdkDragMove, moveItemInArray } from '@angular/cdk/drag-drop';
import { filter, Observable, Subscription } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';

import { BrandIdentityService } from '../../services/brand-identity.service';
import { BrandIdentity } from '../../models/brand-identity';

@Component({
    selector: 'agpr-manage-brand-identities-dialog',
    templateUrl: './manage-brand-identities-dialog.component.html',
    styleUrls: ['./manage-brand-identities-dialog.component.scss'],
    standalone: false
})
export class ManageBrandIdentitiesDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    private subscriptions: Subscription[] = [];

    @Input() id: string = 'manageBrandIdentitiesDialog';
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    hideSlideOverlay: boolean = false;
    loading: boolean = false;
    isInit: boolean = true;
    brandList: BrandIdentity[] = [];
    competitorRecord: Record<number, string>;
    competitorOfRecord: Record<number, string>;
    searchText: string = '';
    filteredBrandList: BrandIdentity[] = [];
    selectedBrand: BrandIdentity = new BrandIdentity();
    manualDropIndex: number;

    @ViewChild('brandListScrollPanel') brandListScrollPanel: ScrollPanelComponent;
    @ViewChildren('brandItem') brandItems: QueryList<ElementRef>;

    constructor(
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public brandIdentityService: BrandIdentityService
    ) {
        super(coreDialogService, 'ManageBrandIdentities');
    }

    ngOnInit(): void {
        this.loading = true;
        this.subscriptions.push(
            this.brandIdentityService.currentResource$.pipe(
                filter(brand => !!brand),
            ).subscribe(brand => {
                const isNewCreated = !this.selectedBrand?.id && brand.id;
                this.selectedBrand = brand;
                if (isNewCreated) {
                    requestAnimationFrame(() => this.scrollBrandIntoView(brand.id));
                }
            }),
            this.brandIdentityService.all$.pipe(
                filter(x => !!x)
            ).subscribe(brands => {
                this.brandList = brands.sort((a, b) => a.orderId - b.orderId);
                this.setUpCompetitorsTooltips();
                if (this.isInit) {
                    if (this.selectedBrand?.id) {
                        const selectedBrand = this.brandList.find(x => x.id === this.selectedBrand.id);
                        if (selectedBrand) {
                            requestAnimationFrame(() => this.scrollBrandIntoView(selectedBrand.id));
                        }
                    } else if (!this.selectedBrand?.name && this.brandList.length > 0) {
                        const defaultSelectedBrand = this.brandList[0];
                        this.selectBrand(defaultSelectedBrand);
                        requestAnimationFrame(() => this.scrollBrandIntoView(defaultSelectedBrand.id));
                    }
                }
                this.filterBrandIdentities();
                this.isInit = false;
                this.loading = false;
            })
        );
    }

    private setUpCompetitorsTooltips() {
        this.competitorRecord = {};
        this.competitorOfRecord = {};
        const competitorOfMap: Map<number, Set<BrandIdentity>> = new Map();

        this.brandList.forEach(brand => {
            const competitors = brand.competitors ?? [];

            const competitorTooltip = competitors
                .filter(x => !!x.id)
                .map(competitor => `<a href="#" data-id="${competitor.id}">${competitor.name}</a>`)
                .join(', ');
            this.competitorRecord[brand.id] = competitorTooltip ? this.localizationService.transform(
                'Common.brandIdentity.manageDialog.competitorTooltip',
                { competitors: competitorTooltip }
            ) : '';

            competitors.forEach(competitor => {
                if (!competitor.id) {
                    return;
                }
                if (!competitorOfMap.has(competitor.id)) {
                    competitorOfMap.set(competitor.id, new Set());
                }
                competitorOfMap.get(competitor.id).add(brand);
            });
        });

        this.brandList.forEach(brand => {
            const competitorOfSet = competitorOfMap.get(brand.id);

            if (!competitorOfSet) {
                this.competitorOfRecord[brand.id] = '';
                return;
            }

            const competitorOfTooltip = Array.from(competitorOfSet)
                .map(competitor => `<a href="#" data-id="${competitor.id}">${competitor.name}</a>`)
                .join(', ');
            this.competitorOfRecord[brand.id] = this.localizationService.transform(
                'Common.brandIdentity.manageDialog.competitorOfTooltip',
                { competitorOfs: competitorOfTooltip }
            );
        });
    }

    competitorTooltipHandler = (event: MouseEvent) => {
        event.preventDefault();
        const brand = this.brandList.find(x => x.id === +(event?.target as any)?.dataset?.id);
        if (brand) {
            this.selectBrand(brand);
            requestAnimationFrame(() => this.scrollBrandIntoView(brand.id));
        }
    };

    private scrollBrandIntoView(id: number | undefined) {
        const brand = this.brandList.find(x => x.id === id);
        if (!brand) {
            return;
        }

        const element = this.brandItems.find(
            el => +el.nativeElement.dataset.id === brand.id
        );

        this.brandListScrollPanel.scrollIntoViewNative(element.nativeElement);
    }

    onDragMoved(event: CdkDragMove<any>) {
        const scrollPanel = this.brandListScrollPanel.sp.contentViewChild.nativeElement;
        if (!scrollPanel) {
            return;
        }

        // auto scroll
        const pointerY = event.pointerPosition.y;
        const rect = scrollPanel.getBoundingClientRect();
        const threshold = 10; // pixels from top/bottom to trigger scroll
        const step = 90; // pixels to scroll per event

        if (pointerY < rect.top + threshold) {
            scrollPanel.scrollBy({top: -step, behavior: 'smooth'});
        } else if (pointerY > rect.bottom - threshold) {
            scrollPanel.scrollBy({top: step, behavior: 'smooth'});
        }

        const draggedEl = event.source.element.nativeElement;
        // exclude the dragged element
        const items = this.brandItems
            .toArray()
            .map(el => el.nativeElement)
            .filter(el => el !== draggedEl);

        // manually calculate drop index
        let newIndex = items.findIndex(el => {
            const r = el.getBoundingClientRect();
            return pointerY < r.top + r.height / 2;
        });

        if (newIndex === -1) {
            newIndex = items.length;
        }
        this.manualDropIndex = newIndex;
    }

    filterBrandIdentities() {
        this.filteredBrandList = this.brandList.filter(
            brand => brand.name.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
        );
    }

    addBrand() {
        this.brandIdentityService.setCurrentResource(new BrandIdentity());
    }

    moveUp(index: number) {
        moveItemInArray(this.brandList, index, index - 1);
        this.setOrder(index - 1, index);
    }

    moveDown(index: number) {
        moveItemInArray(this.brandList, index, index + 1);
        this.setOrder(index + 1, index);

    }

    drop(event: CdkDragDrop<BrandIdentity[]>) {
        const previousIndex = event.previousIndex;
        const currentIndex = this.manualDropIndex ?? event.currentIndex;
        moveItemInArray(this.brandList, previousIndex, currentIndex);
        this.setOrder(currentIndex, previousIndex);
        this.manualDropIndex = null;
    }

    setOrder(currentIndex: number, previousIndex: number) {
        const start = Math.min(currentIndex, previousIndex);
        const end = Math.max(currentIndex, previousIndex);

        const brandList: BrandIdentity[] = [];
        for(let i = start; i <= end; i++) {
            const clonedBrand = this.brandList[i].clone();
            clonedBrand.orderId = i;
            brandList.push(clonedBrand);
        }
        this.brandIdentityService.setOrder(this.brandList[currentIndex], brandList).subscribe();
    }

    togglePrimary(brand: BrandIdentity) {
        const clonedBrand = brand.clone();
        clonedBrand.primary = !brand.primary;

        this.brandIdentityService.update(clonedBrand).subscribe();
    }

    selectBrand(brand: BrandIdentity) {
        this.brandIdentityService.setCurrentResource(brand);
    }

    onSelectBrand(id: number) {
        const brand = this.brandList.find(x => x.id === id);
        if (brand) {
            this.selectBrand(brand);
            requestAnimationFrame(() => this.scrollBrandIntoView(brand.id));
        }
    }

    delete(brand: BrandIdentity) {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Common.brandIdentity.delete.confirmDelete.title',
                alertMessage: 'Common.brandIdentity.delete.confirmDelete.alertMessage',
                message: this.localizationService.transform('Common.brandIdentity.delete.confirmDelete.message', { name: brand.name }),
                alertType: messageSeverityEnum.warning,
                okLabel: 'Core.button.delete',
                id: 'BrandIdentityDelete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.brandIdentityService.delete(brand).subscribe(() => {
                            if (this.selectedBrand?.id === brand.id) {
                                this.selectBrand(new BrandIdentity());
                            }
                        });
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    close() {
        this.hideSlideOverlay = true;
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.closeDialog();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
